import {
	Banknote,
	CalendarDays,
	Check,
	Compass,
	Download,
	Megaphone,
	Scale,
	Sparkles,
	Star,
	Users,
	UserX,
	Wallet,
} from "lucide-react";
import { BookingFab } from "#/components/booking-fab";
import { FeedbackVideosSection } from "#/components/feedback-videos";
import { MessengerFloat, WhatsAppFloat } from "#/components/contact-floats";
import { LanguageSwitcher } from "#/components/language-switcher";
import { LeadForm } from "#/components/lead-form";
import { Logo, LogoMark } from "#/components/logo";
import { useSectionTracking } from "#/components/page-analytics";
import { PartnersMarquee } from "#/components/partners-marquee";
import { ShortsPlayer } from "#/components/shorts-player";
import { Accordion, ForwardArrow, Reveal } from "#/components/ui";
import type { FeedbackVideo } from "#/lib/feedback-videos";
import type { Dict, Locale } from "#/lib/i18n";
import { useI18n } from "#/lib/i18n";

/**
 * Player chrome copy. Deliberately not part of `Dict`: that type doubles as the
 * required shape of every Training's `content` JSON, so adding keys to it would
 * leave already-seeded trainings rendering `undefined`. Same reasoning as
 * `UI_STRINGS` in src/lib/i18n.tsx.
 */
const VIDEO_UI: Record<
	Locale,
	{ label: string; hint: string; caption: string; unmute: string }
> = {
	ar: {
		label: "شاهد عرض الدورة",
		hint: "اضغط للتشغيل",
		caption: "المدرّب يشرح لك البرنامج بنفسه: لمن هو، وبماذا تخرج منه.",
		unmute: "اضغط لتشغيل الصوت",
	},
	fr: {
		label: "Voir la présentation",
		hint: "Appuyez pour lancer",
		caption:
			"Le formateur vous présente lui-même le programme : pour qui, et ce que vous en retirez.",
		unmute: "Activer le son",
	},
	en: {
		label: "Watch the course preview",
		hint: "Tap to play",
		caption:
			"The trainer walks you through the programme himself: who it's for, and what you leave with.",
		unmute: "Tap for sound",
	},
};

const PILLAR_ICONS = [Banknote, Scale, Megaphone];
const PAIN_ICONS = [Compass, Wallet, Scale, UserX];

/** Numbered section label — 01 — LABEL, with a hairline rule. */
function SectionLabel({
	index,
	children,
	tone = "light",
}: {
	index: string;
	children: React.ReactNode;
	tone?: "light" | "dark";
}) {
	return (
		<div className="flex items-center gap-3">
			<span className="index-num font-display text-sm font-bold text-gold">
				{index}
			</span>
			<span
				aria-hidden
				className={`h-px w-8 ${tone === "dark" ? "bg-white/25" : "bg-line"}`}
			/>
			<span
				className={`text-xs font-bold uppercase tracking-[0.18em] ${
					tone === "dark" ? "text-white/70" : "text-muted"
				}`}
			>
				{children}
			</span>
		</div>
	);
}

export interface VideoLandingTemplateProps {
	/** The active locale's content — same shape as the site's i18n `Dict`. */
	content: Dict;
	trainingId: string;
	/** Lets a training ask its own "situation" question in the form. */
	trainingSlug: string;
	/** Null (no video configured, or an unparseable URL) drops the player and
	 *  falls back to the portrait, so the page never renders a dead frame. */
	videoId: string | null;
	/** Optional crisp 9:16 still; null lets the player try YouTube's thumbnail. */
	videoPosterPath: string | null;
	/** Floating contact buttons only render when their training has a value set. */
	messengerUrl: string | null;
	whatsappNumber: string | null;
	/** Per-training photos/PDFs — the route resolves these with a fallback default. */
	heroPortraitPath: string;
	heroWorkshopPath: string;
	trainerAvatarPath: string;
	brochurePdfAr: string;
	brochurePdfFr: string;
	/** Participants' feedback videos — the section is left out when empty. */
	feedbackVideos: FeedbackVideo[];
}

/**
 * Video-first landing template: a vertical (9:16, YouTube-Shorts ratio)
 * presentation of the training carries the hero, and the rest of the page runs
 * through the same `Dict` content the default template uses — so switching a
 * training between templates needs no content migration.
 *
 * The hero stacks (copy → player → CTAs) up to `md`, where it becomes two
 * columns with the player beside the copy. See `.shorts-frame` in styles.css
 * for how the 9:16 frame is sized at each breakpoint.
 */
export function VideoLandingTemplate({
	content: t,
	trainingId,
	trainingSlug,
	videoId,
	videoPosterPath,
	messengerUrl,
	whatsappNumber,
	heroPortraitPath,
	heroWorkshopPath,
	trainerAvatarPath,
	brochurePdfAr,
	brochurePdfFr,
	feedbackVideos,
}: VideoLandingTemplateProps) {
	const { locale } = useI18n();
	const v = VIDEO_UI[locale];

	// Reports every `[data-section]` block below: reach, order and dwell time.
	useSectionTracking();

	return (
		<div className="overflow-x-clip bg-canvas">
			{/* ---------- header ---------- */}
			<header className="sticky top-0 z-50 border-b border-line/70 bg-canvas/80 backdrop-blur-xl">
				<div className="mx-auto flex max-w-6xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
					<Logo size="sm" />
					{/* From `md` there is room for the full nav — it used to appear only
					    at `lg`, leaving tablets with an empty header bar. */}
					<nav className="hidden items-center gap-0.5 md:flex lg:gap-1">
						{[
							{ href: "#course", label: t.pillars.eyebrow },
							{ href: "#trainer", label: t.nav.trainer },
							{ href: "#learn", label: t.nav.outcomes },
							{ href: "#faq", label: t.nav.faq },
						].map((link) => (
							<a
								key={link.href}
								href={link.href}
								data-track={`nav_${link.href.replace("#", "")}`}
								data-track-type="nav"
								data-track-section="header"
								className="rounded-full px-3 py-2 text-sm font-semibold text-muted transition-colors hover:text-navy lg:px-4"
							>
								{link.label}
							</a>
						))}
					</nav>
					<div className="flex items-center gap-2">
						<LanguageSwitcher />
						<a
							href="#register"
							data-track="header_book"
							data-track-type="schedule"
							data-track-section="header"
							className="group hidden items-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white shadow-card transition-colors hover:bg-teal sm:inline-flex"
						>
							{t.nav.book}
							<ForwardArrow className="h-3.5 w-3.5 transition-transform rtl:group-hover:-translate-x-0.5 ltr:group-hover:translate-x-0.5" />
						</a>
					</div>
				</div>
			</header>

			<main>
				{/* ---------- hero: the video carries it ---------- */}
				<section data-section="hero" className="grain relative overflow-hidden">
					<div
						aria-hidden
						className="pointer-events-none absolute inset-0 bg-[radial-gradient(60%_60%_at_85%_-5%,rgb(23_166_177/0.16),transparent_55%),radial-gradient(50%_50%_at_5%_15%,rgb(224_166_60/0.12),transparent_55%)]"
					/>
					{/* `grid-rows-[auto_1fr]`: the player spans both left rows and is the
					    tallest thing here, so with two auto rows the slack would land
					    *between* the headline and the copy below it. Pinning row 1 to
					    its content and giving row 2 the remainder keeps them a single
					    gap apart, with the slack below. */}
					<div className="relative mx-auto grid max-w-6xl gap-8 px-4 pb-14 pt-10 sm:gap-10 sm:px-6 sm:pb-16 md:grid-cols-[1.05fr_0.95fr] md:grid-rows-[auto_1fr] md:items-center md:gap-10 lg:gap-12 lg:px-8 lg:pb-24 lg:pt-16">
						{/* headline */}
						<div className="animate-fade-up md:col-start-1 md:row-start-1">
							<span className="inline-flex items-center gap-2 rounded-full border border-line bg-white/70 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.14em] text-navy shadow-xs backdrop-blur">
								<span className="relative flex h-2 w-2">
									<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-gold opacity-70" />
									<span className="relative inline-flex h-2 w-2 rounded-full bg-gold" />
								</span>
								{t.hero.eyebrow}
							</span>

							<h1 className="mt-5 font-display font-bold leading-[1.05] text-navy text-[clamp(2.2rem,6.2vw,4.2rem)] sm:mt-6">
								{t.hero.titleLead}{" "}
								<span className="relative inline-block">
									<span className="relative z-10 text-teal">
										{t.hero.titleHighlight}
									</span>
									<span
										aria-hidden
										className="absolute inset-x-[-0.1em] bottom-[0.08em] z-0 h-[0.42em] origin-[right] rounded-xs bg-gold/30 ltr:origin-[left]"
										style={{
											animation:
												"marker 0.7s 0.35s cubic-bezier(0.16,1,0.3,1) both",
										}}
									/>
								</span>
							</h1>

							{t.hero.date ? (
								<p className="mt-3 inline-flex items-center gap-2 text-sm font-bold text-navy">
									<CalendarDays className="h-4 w-4 text-teal" />
									{t.hero.date}
								</p>
							) : null}
						</div>

						{/* the vertical player — first thing a phone sees under the title */}
						<div className="animate-fade-up [animation-delay:120ms] md:col-start-2 md:row-span-2 md:row-start-1 md:self-center">
							<div className="shorts-frame relative">
								<div
									aria-hidden
									className="absolute -inset-2.5 -rotate-2 rounded-[2.5rem] border-2 border-gold/45 sm:-inset-3"
								/>
								{videoId ? (
									<ShortsPlayer
										videoId={videoId}
										poster={videoPosterPath}
										fallbackPoster={heroPortraitPath}
										label={v.label}
										hint={v.hint}
										autoplay
										unmuteLabel={v.unmute}
									/>
								) : (
									// No video configured: the frame keeps its shape and shows
									// the trainer portrait instead of collapsing the layout.
									<div className="grain relative aspect-9/16 w-full overflow-hidden rounded-[2rem] bg-navy shadow-lift">
										<img
											src={heroPortraitPath}
											alt={t.hero.portraitAlt}
											width={700}
											height={1244}
											className="absolute inset-0 h-full w-full object-cover object-top"
										/>
										<div className="absolute inset-x-0 bottom-0 bg-linear-to-t from-navy-900 to-transparent p-6 pt-16">
											<div className="font-display text-lg font-bold text-white">
												{t.trainer.name}
											</div>
											<div className="text-sm text-teal-100">
												{t.trainer.role}
											</div>
										</div>
									</div>
								)}
							</div>

							<p className="mx-auto mt-5 max-w-xs text-center text-sm leading-relaxed text-muted">
								<Sparkles className="me-1.5 inline h-3.5 w-3.5 text-gold" />
								{v.caption}
							</p>
						</div>

						{/* The subtitle and CTAs live below the player rather than in the
						    headline block, so a phone reaches the video within the first
						    screen instead of scrolling past a paragraph to find it. From
						    `md` the explicit grid placement restores the reading order. */}
						<div className="animate-fade-up [animation-delay:200ms] md:col-start-1 md:row-start-2 md:self-start">
							<p className="mb-7 max-w-xl text-base leading-relaxed text-muted sm:text-lg lg:mb-8">
								{t.hero.subtitle}
							</p>

							<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
								<a
									href="#register"
									data-track="hero_primary"
									data-track-type="schedule"
									data-track-section="hero"
									className="group inline-flex w-full items-center justify-center gap-2.5 rounded-full bg-teal-700 px-7 py-4 text-base font-bold text-white shadow-glow transition-all hover:-translate-y-0.5 hover:bg-teal-600 sm:w-auto lg:px-8"
								>
									{t.hero.ctaPrimary}
									<ForwardArrow className="h-4 w-4 transition-transform rtl:group-hover:-translate-x-1 ltr:group-hover:translate-x-1" />
								</a>
								<a
									href="#course"
									data-track="hero_secondary"
									data-track-type="nav"
									data-track-section="hero"
									className="inline-flex w-full items-center justify-center gap-2 rounded-full border border-navy/15 bg-white/60 px-6 py-4 text-base font-bold text-navy transition-colors hover:border-navy/30 hover:bg-white sm:w-auto lg:px-7"
								>
									{t.hero.ctaSecondary}
								</a>
							</div>

							<ul className="mt-7 flex flex-col gap-3 lg:mt-8">
								{t.hero.bullets.map((b) => (
									<li key={b} className="flex items-center gap-3">
										<span className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-teal/12 text-teal">
											<Check className="h-3.5 w-3.5" strokeWidth={3} />
										</span>
										<span className="text-[0.95rem] font-medium text-ink">
											{b}
										</span>
									</li>
								))}
							</ul>

							<div className="mt-7 flex items-center gap-3">
								<div className="flex text-gold">
									{[0, 1, 2, 3, 4].map((i) => (
										<Star key={i} className="h-4 w-4 fill-current" />
									))}
								</div>
								<span className="text-sm font-medium text-muted">
									{t.hero.trust}
								</span>
							</div>
						</div>
					</div>
				</section>

				{/* ---------- stats band ---------- */}
				<section data-section="stats" className="px-4 sm:px-6 lg:px-8">
					<div className="grain relative mx-auto -mt-2 max-w-6xl overflow-hidden rounded-4xl bg-navy px-6 py-8 shadow-lift sm:px-10 sm:py-10">
						<div
							aria-hidden
							className="pointer-events-none absolute inset-0 bg-[radial-gradient(50%_120%_at_50%_-20%,rgb(23_166_177/0.25),transparent_60%)]"
						/>
						{/* Five stats: 2-up on phones, all five in a row from `md` where
						    there's width for it — `lg` used to be the only step, which
						    left tablets with three half-empty rows. */}
						<div className="relative grid grid-cols-2 gap-y-7 md:grid-cols-5 md:gap-y-0">
							{t.stats.map((stat, i) => (
								<div
									key={stat.label}
									// The odd fifth stat is alone on the last 2-column row, so
									// it spans both and stays centred.
									className={`px-2 text-center sm:px-3 lg:px-4 max-md:last:col-span-2 ${
										i > 0 ? "md:border-s md:border-white/12" : ""
									}`}
								>
									<div className="font-display text-3xl font-bold text-white sm:text-4xl lg:text-5xl">
										{stat.value}
									</div>
									<div className="mt-1.5 text-xs text-white/60 sm:text-sm">
										{stat.label}
									</div>
								</div>
							))}
						</div>
					</div>
				</section>

				{/* ---------- pains ---------- */}
				<section
					data-section="pains"
					className="mx-auto max-w-6xl px-4 py-14 sm:px-6 sm:py-16 lg:px-8 lg:py-24"
				>
					<Reveal className="max-w-2xl">
						<SectionLabel index="01">{t.pains.eyebrow}</SectionLabel>
						<h2 className="mt-5 font-display text-3xl font-bold leading-tight text-navy sm:text-4xl lg:text-[2.75rem]">
							{t.pains.heading}
						</h2>
						<p className="mt-4 text-base text-muted">{t.pains.sub}</p>
					</Reveal>

					{/* Swipeable on a phone, a 2-up grid from `sm`, four across from
					    `lg` — the cards keep their full copy instead of shrinking to
					    fit four across a 390px screen. */}
					<div className="-mx-4 mt-10 flex snap-x snap-mandatory gap-4 overflow-x-auto px-4 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden sm:mx-0 sm:grid sm:grid-cols-2 sm:overflow-visible sm:px-0 sm:pb-0 lg:grid-cols-4">
						{t.pains.items.map((pain, i) => {
							const Icon = PAIN_ICONS[i];
							return (
								<article
									key={pain.title}
									className="group relative w-[78%] flex-none snap-center overflow-hidden rounded-2xl border border-line bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-card sm:w-auto"
								>
									<span
										aria-hidden
										className="index-num absolute inset-e-4 top-3 font-display text-4xl font-bold text-line transition-colors group-hover:text-gold/40"
									>
										{`0${i + 1}`}
									</span>
									<span className="flex h-11 w-11 items-center justify-center rounded-xl bg-rose-50 text-rose-500">
										<Icon className="h-5 w-5" />
									</span>
									<h3 className="mt-5 font-display text-lg font-bold text-navy">
										{pain.title}
									</h3>
									<p className="mt-1.5 text-sm leading-relaxed text-muted">
										{pain.text}
									</p>
								</article>
							);
						})}
					</div>

					<Reveal className="mt-8 flex items-center justify-center gap-3 rounded-2xl bg-navy px-5 py-5 text-center sm:px-6">
						<span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-teal text-white">
							<Check className="h-4 w-4" strokeWidth={3} />
						</span>
						<p className="font-display text-base font-bold text-white sm:text-lg">
							{t.pains.solution}
						</p>
					</Reveal>
				</section>

				{/* ---------- pillars (solution) ---------- */}
				<section
					id="course"
					data-section="course"
					className="scroll-mt-20 bg-white py-14 sm:py-20 lg:py-24"
				>
					<div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
						<Reveal className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
							<div className="flex flex-col items-start gap-4">
								<SectionLabel index="02">{t.pillars.eyebrow}</SectionLabel>
								<h2 className="max-w-lg font-display text-3xl font-bold leading-tight text-navy sm:text-4xl lg:text-[2.75rem]">
									{t.pillars.heading}
								</h2>
							</div>
							<p className="max-w-xs text-sm leading-relaxed text-muted">
								{t.pillars.sub}
							</p>
						</Reveal>

						{/* Straight from one column to three at `md`. A 2-up step at `sm`
						    would leave the third card orphaned on its own row for the
						    640–767px band only, which reads worse than full-width cards. */}
						<div className="mt-10 grid gap-5 md:grid-cols-3 lg:mt-12">
							{t.pillars.items.map((pillar, i) => {
								const Icon = PILLAR_ICONS[i];
								return (
									<Reveal key={pillar.title}>
										<article className="group relative h-full overflow-hidden rounded-[1.75rem] bg-canvas p-6 ring-1 ring-line transition-all duration-300 hover:-translate-y-1.5 hover:bg-navy hover:shadow-lift sm:p-7">
											<span
												aria-hidden
												className="absolute inset-x-7 top-0 h-1 origin-[right] scale-x-0 rounded-b bg-gold transition-transform duration-300 group-hover:scale-x-100 ltr:origin-[left]"
											/>
											<div className="flex items-center justify-between">
												<span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal text-white transition-transform group-hover:scale-110">
													<Icon className="h-5 w-5" />
												</span>
												<span className="index-num font-display text-3xl font-bold text-line transition-colors group-hover:text-gold">
													{`0${i + 1}`}
												</span>
											</div>
											<h3 className="mt-10 font-display text-xl font-bold text-navy transition-colors group-hover:text-white lg:mt-14">
												{pillar.title}
											</h3>
											<p className="mt-2 text-sm leading-relaxed text-muted transition-colors group-hover:text-white/60">
												{pillar.text}
											</p>
										</article>
									</Reveal>
								);
							})}
						</div>

						{/* technical-card downloads */}
						<Reveal className="mt-10 flex flex-col items-center justify-center gap-3 rounded-[1.75rem] bg-canvas p-6 ring-1 ring-line sm:flex-row">
							<span className="text-center text-sm font-bold text-navy sm:text-start">
								{t.pillars.downloadLabel}
							</span>
							<div className="flex flex-wrap items-center justify-center gap-3">
								<a
									href={brochurePdfAr}
									download
									data-track="download_fiche_ar"
									data-track-type="download"
									data-track-section="course"
									data-track-locale="ar"
									className="inline-flex items-center gap-2 rounded-full bg-teal-700 px-5 py-2.5 text-sm font-bold text-white shadow-glow transition-all hover:-translate-y-0.5 hover:bg-teal-600"
								>
									<Download className="h-4 w-4" />
									عربية (AR)
								</a>
								<a
									href={brochurePdfFr}
									download
									data-track="download_fiche_fr"
									data-track-type="download"
									data-track-section="course"
									data-track-locale="fr"
									className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-bold text-navy ring-1 ring-line transition-colors hover:bg-teal-50"
								>
									<Download className="h-4 w-4" />
									Français (FR)
								</a>
							</div>
						</Reveal>
					</div>
				</section>

				{/* ---------- references ---------- */}
				<PartnersMarquee partners={t.partners} />

				{/* ---------- trainer ---------- */}
				<section
					id="trainer"
					data-section="trainer"
					className="scroll-mt-20 py-14 sm:py-20 lg:py-24"
				>
					{/* Two columns from `md`. Below that the avatar is capped rather
					    than full-bleed — an `aspect-square` image at 768px wide would
					    otherwise be 768px tall. */}
					<div className="mx-auto grid max-w-6xl gap-10 px-4 sm:px-6 md:grid-cols-[0.85fr_1.15fr] md:items-start lg:gap-12 lg:px-8">
						<Reveal className="order-2 mx-auto w-full max-w-sm md:order-1 md:max-w-none">
							<div className="relative">
								<div
									aria-hidden
									className="absolute -inset-2.5 rotate-2 rounded-[2.3rem] border-2 border-teal/25"
								/>
								<div className="relative overflow-hidden rounded-4xl bg-navy shadow-lift">
									<img
										src={trainerAvatarPath}
										alt={t.trainer.avatarAlt}
										width={460}
										height={460}
										className="aspect-square w-full object-cover"
									/>
									<div className="absolute inset-x-0 bottom-0 bg-linear-to-t from-navy via-navy/70 to-transparent p-6 pt-16">
										<div className="font-display text-xl font-bold text-white">
											{t.trainer.name}
										</div>
										<div className="text-sm text-teal-100">
											{t.trainer.role}
										</div>
									</div>
								</div>
							</div>

							<div className="mt-6 flex items-center gap-2.5 rounded-2xl bg-white p-3 pe-4 shadow-card ring-1 ring-line">
								<span className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gold-50 text-gold-600">
									<Users className="h-4 w-4" />
								</span>
								<div>
									<div className="text-sm font-bold text-navy">
										{t.trainer.statValue}
									</div>
									<div className="text-[0.7rem] text-muted">
										{t.trainer.statLabel}
									</div>
								</div>
							</div>
						</Reveal>

						<div className="order-1 md:order-2">
							<Reveal className="flex flex-col items-start gap-4">
								<SectionLabel index="03">{t.trainer.eyebrow}</SectionLabel>
								<h2 className="font-display text-3xl font-bold leading-tight text-navy sm:text-4xl lg:text-[2.75rem]">
									{t.trainer.heading}
								</h2>
								{/* whitespace-pre-line: a `\n` typed into the content JSON
								    should break the paragraph, not collapse to a space like
								    plain HTML text does by default. */}
								<p className="whitespace-pre-line text-base leading-relaxed text-muted">
									{t.trainer.bio}
								</p>
							</Reveal>

							<Reveal className="mt-8">
								<Accordion items={t.trainer.why} group="trainer" />
							</Reveal>
						</div>
					</div>
				</section>

				{/* ---------- outcomes ---------- */}
				<section
					id="learn"
					data-section="learn"
					className="scroll-mt-20 bg-white py-14 sm:py-20 lg:py-24"
				>
					<div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
						<Reveal className="mb-6 overflow-hidden rounded-4xl bg-teal-50 p-6 sm:p-10 lg:mb-8">
							<div className="mb-5 flex items-center gap-3">
								<span className="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-teal text-white">
									<Compass className="h-5 w-5" />
								</span>
								<h2 className="font-display text-2xl font-bold leading-tight text-navy sm:text-3xl">
									{t.outcomes.audienceHeading}
								</h2>
							</div>
							<div className="flex flex-wrap gap-2.5">
								{t.outcomes.audience.map((item) => (
									<span
										key={item}
										className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-navy ring-1 ring-teal/15"
									>
										{item}
									</span>
								))}
							</div>
						</Reveal>

						<div className="grid gap-5 md:grid-cols-[1.2fr_1fr]">
							<Reveal className="rounded-4xl bg-navy p-6 sm:p-10">
								<div className="mb-6">
									<SectionLabel index="04" tone="dark">
										{t.outcomes.objectivesHeading}
									</SectionLabel>
								</div>
								<ul className="flex flex-col gap-4">
									{t.outcomes.objectives.map((item, i) => (
										<li key={item} className="flex items-start gap-4">
											<span className="index-num mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full bg-gold/15 font-display text-xs font-bold text-gold">
												{i + 1}
											</span>
											<span className="text-sm leading-relaxed text-white/80">
												{item}
											</span>
										</li>
									))}
								</ul>
							</Reveal>

							<Reveal className="flex flex-col justify-center rounded-4xl bg-gold-50 p-6 ring-1 ring-gold/20 sm:p-8">
								<div className="font-display text-5xl font-bold text-gold-600 sm:text-6xl">
									{t.outcomes.practiceValue}
								</div>
								<p className="mt-3 text-sm leading-relaxed text-ink/70">
									{t.outcomes.practiceNote}
								</p>
							</Reveal>
						</div>
					</div>
				</section>

				<FeedbackVideosSection
					videos={feedbackVideos}
					fallbackPoster={heroPortraitPath}
				/>

				{/* ---------- faq ---------- */}
				<section
					id="faq"
					data-section="faq"
					className="scroll-mt-20 py-14 sm:py-20 lg:py-24"
				>
					<div className="mx-auto grid max-w-6xl gap-8 px-4 sm:px-6 md:grid-cols-[0.85fr_1.15fr] lg:gap-10 lg:px-8">
						<Reveal className="flex flex-col items-start gap-4">
							<SectionLabel index="05">{t.faq.eyebrow}</SectionLabel>
							<h2 className="font-display text-3xl font-bold leading-tight text-navy sm:text-4xl lg:text-[2.75rem]">
								{t.faq.heading}
							</h2>
							<p className="text-sm leading-relaxed text-muted">{t.faq.sub}</p>
						</Reveal>
						<Reveal>
							<Accordion items={t.faq.items} group="faq" />
						</Reveal>
					</div>
				</section>

				{/* ---------- register ---------- */}
				<section
					id="register"
					data-section="register"
					className="scroll-mt-20 px-4 pb-16 sm:px-6 lg:px-8 lg:pb-20"
				>
					<div className="grain relative mx-auto max-w-6xl overflow-hidden rounded-[2.5rem] bg-navy">
						<div
							aria-hidden
							className="pointer-events-none absolute inset-0 bg-[radial-gradient(50%_60%_at_15%_0%,rgb(23_166_177/0.22),transparent_60%),radial-gradient(40%_50%_at_100%_100%,rgb(224_166_60/0.14),transparent_60%)]"
						/>
						{/* The side photo only earns its place at `lg` — below that the
						    form takes the full card rather than being squeezed. */}
						<div className="relative grid lg:grid-cols-[1fr_0.85fr]">
							<div className="p-6 sm:p-12 lg:p-14">
								<SectionLabel index="06" tone="dark">
									{t.register.eyebrow}
								</SectionLabel>
								<h2 className="mt-5 max-w-md font-display text-3xl font-bold leading-tight text-white sm:text-4xl">
									{t.register.heading}
								</h2>
								<p className="mt-4 max-w-sm text-sm leading-relaxed text-white/60">
									{t.register.sub}
								</p>

								<div className="mt-8 rounded-[1.75rem] bg-canvas p-5 sm:p-7">
									<LeadForm
										source="landing-register"
										trainingId={trainingId}
										trainingSlug={trainingSlug}
									/>
								</div>
							</div>

							<div className="relative hidden lg:block">
								<img
									src={heroWorkshopPath}
									alt={t.register.imageAlt}
									width={560}
									height={686}
									className="h-full w-full object-cover opacity-90"
								/>
								<div
									aria-hidden
									className="absolute inset-0 bg-linear-to-l from-transparent to-navy rtl:bg-linear-to-r"
								/>
							</div>
						</div>
					</div>
				</section>
			</main>

			{/* ---------- footer ---------- */}
			<footer data-section="footer" className="bg-navy-900 py-12">
				<div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
					<div className="flex flex-col items-center gap-6 border-b border-white/10 pb-8 text-center sm:flex-row sm:justify-between sm:text-start">
						<LogoMark className="h-9 w-auto" />
						<p className="max-w-xs text-sm text-white/50">{t.footer.tagline}</p>
						<a
							href="#register"
							data-track="footer_book"
							data-track-type="schedule"
							data-track-section="footer"
							className="rounded-full bg-teal-700 px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-teal-600"
						>
							{t.footer.book}
						</a>
					</div>
					<div className="flex flex-col items-center justify-between gap-3 pt-6 text-xs text-white/60 sm:flex-row">
						<p>{t.footer.rights}</p>
						<p dir="ltr">moustachiracademy@moustachir.dz · www.moustachir.dz</p>
					</div>
				</div>
			</footer>

			{messengerUrl && <MessengerFloat t={t} messengerUrl={messengerUrl} />}
			{whatsappNumber && (
				<WhatsAppFloat t={t} whatsappNumber={whatsappNumber} />
			)}
			<BookingFab href="#register" />
		</div>
	);
}
